import { Directive, ElementRef, forwardRef, HostListener, Input } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

export interface CurrencyMaskOptions {
  align?: 'left' | 'right' | 'center';
  prefix?: string;
  suffix?: string;
  thousands?: string;
  decimal?: string;
  precision?: number;
  allowNegative?: boolean;
  /** Clearing the field gives `null` instead of `0`. */
  nullable?: boolean;
}

const DEFAULT_OPTIONS: Required<CurrencyMaskOptions> = {
  align: 'left',
  prefix: '',
  suffix: '',
  thousands: '.',
  decimal: ',',
  precision: 2,
  allowNegative: true,
  nullable: false,
};

/**
 * Money input with cents-first entry, as ngx-currency's financial mode had it: digits fill in from the right, so
 * typing 5 shows 0,05 and a following 1 shows 0,51. The browser applies every edit (typing, paste, cut, IME); the
 * `input` handler then rebuilds the value from the digits and puts the caret back by counting digits to its right.
 */
@Directive({
  selector: 'input[currencyMask]',
  standalone: true,
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => CurrencyMaskDirective), multi: true }],
})
export class CurrencyMaskDirective implements ControlValueAccessor {
  private opts = DEFAULT_OPTIONS;
  private value: number | null = null;
  private negative = false;
  private onChange: (value: number | null) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  constructor(private el: ElementRef<HTMLInputElement>) {
    this.el.nativeElement.style.textAlign = this.opts.align;
  }

  @Input() set options(options: CurrencyMaskOptions | null | undefined) {
    this.opts = { ...DEFAULT_OPTIONS, ...options };
    this.el.nativeElement.style.textAlign = this.opts.align;
    this.render();
  }

  writeValue(value: unknown): void {
    const number = value === null || value === undefined || value === '' ? NaN : Number(value);
    this.value = isNaN(number) ? null : number;
    this.negative = this.value !== null && (this.value < 0 || Object.is(this.value, -0));
    this.render();
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.el.nativeElement.disabled = isDisabled;
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    const { isComposing, inputType } = event as InputEvent;
    if (isComposing) return;
    this.applyEdit(inputType ?? '');
  }

  @HostListener('compositionend')
  onCompositionEnd(): void {
    this.applyEdit('insertCompositionText');
  }

  // A separator holds no digit, so a deletion that would only remove one skips over it to the next digit.
  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    const input = this.el.nativeElement;
    if (input.selectionStart !== input.selectionEnd || (event.key !== 'Backspace' && event.key !== 'Delete')) return;
    let caret = input.selectionStart ?? 0;
    if (event.key === 'Backspace') {
      while (caret > this.minCaret() && !/\d/.test(input.value[caret - 1])) caret--;
    } else {
      while (caret < input.value.length - this.opts.suffix.length && !/\d/.test(input.value[caret])) caret++;
    }
    input.setSelectionRange(caret, caret);
  }

  @HostListener('drop', ['$event'])
  onDrop(event: DragEvent): void {
    event.preventDefault();
  }

  @HostListener('blur')
  onBlur(): void {
    this.onTouched();
  }

  private applyEdit(inputType: string): void {
    const input = this.el.nativeElement;
    const raw = input.value;
    const digitsRightOfCaret = (raw.slice(input.selectionEnd ?? raw.length).match(/\d/g) ?? []).length;
    const digits = raw.replace(/\D/g, '');

    // A shown negative always carries its '-', so losing it (or typing '+') makes the amount positive again.
    this.negative = this.opts.allowNegative && raw.includes('-') && !raw.includes('+');

    const deletingZero = inputType.startsWith('delete') && this.value === 0;
    if (!digits && !raw.includes('-')) {
      this.value = this.opts.nullable ? null : 0;
    } else if (this.opts.nullable && deletingZero && !Number(digits)) {
      this.value = null;
      this.negative = false;
    } else {
      const amount = Number(digits || '0') / 10 ** this.opts.precision;
      this.value = this.negative ? -amount : amount;
    }

    this.render();
    if (this.value !== null) this.placeCaret(digitsRightOfCaret);
    this.onChange(this.value);
  }

  private render(): void {
    this.el.nativeElement.value = this.value === null ? '' : this.format(this.value);
  }

  private format(value: number): string {
    const { prefix, suffix, thousands, decimal, precision, allowNegative } = this.opts;
    const [integer, fraction] = Math.abs(value).toFixed(precision).split('.');
    const grouped = integer.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    const sign = this.negative && allowNegative ? '-' : '';
    return sign + prefix + grouped + (precision > 0 ? decimal + fraction : '') + suffix;
  }

  private placeCaret(digitsRight: number): void {
    const input = this.el.nativeElement;
    const text = input.value;
    let caret = text.length - this.opts.suffix.length;
    if (digitsRight > 0) {
      caret = this.minCaret();
      for (let i = text.length - 1, seen = 0; i >= 0; i--) {
        if (/\d/.test(text[i]) && ++seen === digitsRight) {
          caret = Math.max(i, caret);
          break;
        }
      }
    }
    input.setSelectionRange(caret, caret);
  }

  private minCaret(): number {
    return (this.negative && this.opts.allowNegative ? 1 : 0) + this.opts.prefix.length;
  }
}
